import {
  DIRECTION,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
} from 'webclient.constants';

export const SNAP_SPANS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

export function calculateNewWidths({
  startingWidth,
  startingPosition,
  currentPosition,
  startingNextColWidth,
  slideScaleRatio,
}: {
  startingWidth: number;
  startingPosition: number;
  currentPosition: number;
  startingNextColWidth: number;
  slideScaleRatio: number;
}) {
  const changeInPixels = (currentPosition - startingPosition) / slideScaleRatio;
  // round to the nearest .5 to mimic how the browser rounds percentage values
  // this prevents the columns from changing widths slightly
  return {
    newCurrentColWidth: Math.round((startingWidth + changeInPixels) * 2) / 2,
    newNextColWidth:
      Math.round((startingNextColWidth - changeInPixels) * 2) / 2,
  };
}

export function getSnapWidths(totalWidth: number): number[] {
  return SNAP_SPANS.map(s => (s / 12) * totalWidth);
}

export function getClosestNumber(number: number, options: number[]) {
  return options.reduce((prev, curr) => {
    return Math.abs(curr - number) < Math.abs(prev - number) ? curr : prev;
  });
}

export function getNewWidths({
  direction,
  snapWidths,
  startingWidths,
  currentWidths,
  currentColumnIndex,
  startingPosition,
  currentPosition,
  slideScaleRatio,
}: {
  direction: DIRECTION;
  snapWidths: number[];
  startingWidths: number[];
  currentWidths: number[];
  currentColumnIndex: number;
  startingPosition: number;
  currentPosition: number;
  slideScaleRatio: number;
}) {
  const updatedWidths = [...startingWidths];
  const currentColWidth: number = startingWidths[currentColumnIndex];
  const nextColWidth: number = startingWidths[currentColumnIndex + 1];
  const minimumWidth = snapWidths[0];

  if (
    (direction === DIRECTION_LEFT &&
      currentWidths[currentColWidth] === minimumWidth) ||
    (direction === DIRECTION_RIGHT &&
      currentWidths[currentColWidth + 1] === minimumWidth)
  ) {
    return currentWidths;
  }

  const { newCurrentColWidth, newNextColWidth } = calculateNewWidths({
    startingWidth: currentColWidth,
    startingNextColWidth: nextColWidth,
    startingPosition,
    currentPosition,
    slideScaleRatio,
  });

  const hasReachedMinimum = newCurrentColWidth <= minimumWidth;
  const hasReachedMinimumNextCol =
    newNextColWidth !== undefined && newNextColWidth <= minimumWidth;

  if (hasReachedMinimum) {
    const difference = currentColWidth - minimumWidth;
    updatedWidths[currentColumnIndex] = minimumWidth;
    updatedWidths[currentColumnIndex + 1] = nextColWidth + difference;
    return updatedWidths;
  }

  if (hasReachedMinimumNextCol) {
    const difference = nextColWidth - minimumWidth;
    updatedWidths[currentColumnIndex] = currentColWidth + difference;
    updatedWidths[currentColumnIndex + 1] = minimumWidth;
    return updatedWidths;
  }

  updatedWidths[currentColumnIndex] = newCurrentColWidth;
  updatedWidths[currentColumnIndex + 1] = newNextColWidth;

  return updatedWidths;
}

export function moveToNextSnap({
  direction,
  widths,
  currentColumnIndex,
  snapWidths,
}: {
  direction: DIRECTION;
  widths: number[];
  currentColumnIndex: number;
  snapWidths: number[];
}) {
  const updatedWidths = [...widths];
  const minimum = snapWidths[0];
  const maximum = snapWidths.at(-1) as number;

  if (
    direction === DIRECTION_LEFT &&
    updatedWidths[currentColumnIndex] > minimum
  ) {
    updatedWidths[currentColumnIndex] =
      updatedWidths[currentColumnIndex] - minimum;
    updatedWidths[currentColumnIndex + 1] =
      updatedWidths[currentColumnIndex + 1] + minimum;

    return updatedWidths;
  } else if (
    direction === DIRECTION_RIGHT &&
    updatedWidths[currentColumnIndex] < maximum &&
    updatedWidths[currentColumnIndex + 1] > minimum
  ) {
    updatedWidths[currentColumnIndex] =
      updatedWidths[currentColumnIndex] + minimum;
    updatedWidths[currentColumnIndex + 1] =
      updatedWidths[currentColumnIndex + 1] - minimum;

    return updatedWidths;
  }
}
